import React, { Component } from 'react'
import { withRouter } from 'react-router-dom'
import { inject, observer } from 'mobx-react'
import { Table, Row, Col, Tag, Icon, Tooltip, Button, message } from 'antd'
import TransactionDetailsModal from '@components/Popup/TransactionDetailsModal'
import { tranzila } from '@services/api'
import { openStoSearch } from '@utils/stoSearchHandoff'
import {
    parseUserId,
    parseUserName,
    formatPhone,
    formatAmount,
    statusText,
    statusColor,
    operationType,
    normalizeTransactionItems
} from '../utils'
import { TRANSACTION_ITEMS_TERMINALS } from '../Documents/utils'

// antd 3.23 doesn't know column.ellipsis (added in 3.25) — truncate via style, full text in the tooltip.
// without truncation long email/מוצר/name stretch the column and break the sum of widths that scroll.x relies on
const clip = (title, children = null, style = {}) => (
    <Tooltip title={title || ''}>
        <span
            style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ...style
            }}
        >
            {children || title || '—'}
        </span>
    </Tooltip>
)

@inject((store: any) => {
    const { popupStore, collectionsStore, recordsStore, doctorsStore } = store

    return { popupStore, collectionsStore, recordsStore, doctorsStore }
})
@observer
class List extends Component<any, any> {
    state = { detailsTxn: null, pdfLoading: {}, items: [], itemsLoading: false, itemsError: false }
    pdfRequests = {}
    itemsRequestId = 0

    pdfKey = record => `${record.child_terminal || ''}:${record.txnfdid}`

    // The modal opens immediately from the already loaded row; items are loaded by a separate
    // request for ONE transaction — the page endpoints intentionally don't return them.
    // itemsRequestId is the same technique as dataRequestId on the page: a response for transaction A must not
    // end up in the modal of transaction B if B was opened first.
    openDetails = async record => {
        const requestId = ++this.itemsRequestId
        const terminal = record.child_terminal
        const transactionId = record.index
        // Only terminals the items endpoint accepts: any other one answers 400, which the modal would
        // show as a false "could not load" error. Those rows open the modal with no items request.
        const canLoad =
            TRANSACTION_ITEMS_TERMINALS.indexOf(terminal) !== -1 && transactionId != null && transactionId !== ''

        this.setState({ detailsTxn: record, items: [], itemsLoading: canLoad, itemsError: false })
        if (!canLoad) return

        try {
            const res = await tranzila.getTransactionItems({ terminal, transaction_id: transactionId })
            if (requestId !== this.itemsRequestId) return
            const payload = res && res.data
            this.setState({ items: normalizeTransactionItems(payload && payload.items), itemsLoading: false })
        } catch (e) {
            if (requestId !== this.itemsRequestId) return
            console.log(e && e.response && e.response.data)
            // The rest of the modal is already visible and stays usable — only this block broke,
            // so the error lives inside the section rather than closing the window or going to message.error.
            this.setState({ items: [], itemsLoading: false, itemsError: true })
        }
    }

    // Invalidate the pending request: a late response must not show the section after closing.
    closeDetails = () => {
        this.itemsRequestId += 1
        this.setState({ detailsTxn: null, items: [], itemsLoading: false, itemsError: false })
    }

    downloadPdf = async record => {
        const key = this.pdfKey(record)
        if (this.pdfRequests[key]) return

        this.pdfRequests[key] = true
        this.setState(prev => ({ pdfLoading: { ...prev.pdfLoading, [key]: true } }))
        try {
            await tranzila.downloadTransactionPdf({
                txnfdid: record.txnfdid,
                terminal: record.child_terminal
            })
        } catch (e) {
            const status = e && e.response && e.response.status
            message.error(status === 404 ? 'המסמך לא נמצא' : 'הורדת המסמך נכשלה')
        } finally {
            delete this.pdfRequests[key]
            this.setState(prev => {
                const pdfLoading = { ...prev.pdfLoading }
                delete pdfLoading[key]
                return { pdfLoading }
            })
        }
    }

    render() {
        const { list, loading, page, setPage } = this.props
        const { detailsTxn, pdfLoading, items, itemsLoading, itemsError } = this.state
        const columns = [
            {
                title: 'מס׳ עסקה',
                key: 'index',
                width: 110,
                align: 'right',
                render: (_, record) => (
                    <span>
                        {record.index}{' '}
                        <Icon
                            type="info-circle"
                            style={{ color: '#e77910', cursor: 'pointer', marginRight: 4 }}
                            onClick={() => this.openDetails(record)}
                        />
                    </span>
                )
            },
            {
                title: 'סוג',
                key: 'kind',
                width: 140,
                align: 'right',
                render: (_, record) => clip(operationType(record))
            },
            {
                title: 'טרמינל',
                key: 'child_terminal',
                width: 140,
                align: 'right',
                render: (_, record) => <span dir="ltr">{record.child_terminal || '—'}</span>
            },
            {
                title: 'לקוח',
                key: 'client',
                width: 200,
                align: 'right',
                render: (_, record) => {
                    const uid = parseUserId(record.user_defined_2)
                    const name = parseUserName(record.user_defined_2)
                    return uid
                        ? (
                              <span style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                  <span style={{ flex: 1, minWidth: 0 }}>
                                      {clip(
                                          `${name} (${uid})`,
                                          // a real link, not history.push: gives new tab,
                                          // middle click and context menu for free
                                          <a href={`/clients/${uid}`} target="_blank" rel="noopener noreferrer">
                                              {name} <span dir="ltr">({uid})</span>
                                          </a>
                                      )}
                                  </span>
                                  <Tooltip title="חיפוש חיוב בטרנזילה">
                                      <Icon
                                          type="search"
                                          style={{ color: '#1890ff', cursor: 'pointer', marginRight: 6 }}
                                          onClick={() => openStoSearch('clientId', uid)}
                                      />
                                  </Tooltip>
                              </span>
                          )
                        : clip(name)
                }
            },
            {
                title: 'אימייל',
                key: 'email',
                width: 210,
                align: 'right',
                render: (_, record) =>
                    clip(record.user_defined_3, <span dir="ltr">{record.user_defined_3 || '—'}</span>)
            },
            {
                title: 'טלפון',
                key: 'phone',
                width: 120,
                align: 'right',
                render: (_, record) => <span dir="ltr">{formatPhone(record.user_defined_5) || '—'}</span>
            },
            {
                title: 'מוצר',
                key: 'product',
                width: 220,
                align: 'right',
                render: (_, record) => clip(record.user_defined_7)
            },
            {
                title: 'תאריך',
                key: 'transaction_date',
                width: 110,
                align: 'right',
                render: (_, record) => <span>{record.transaction_date || '—'}</span>
            },
            {
                title: 'שעה',
                key: 'transaction_time',
                width: 90,
                align: 'right',
                render: (_, record) => <span>{record.transaction_time || '—'}</span>
            },
            {
                title: 'סטטוס',
                key: 'response',
                width: 220,
                align: 'right',
                render: (_, record) => (
                    <Tag
                        color={statusColor(record.group)}
                        style={{ display: 'block', width: '100%', maxWidth: '100%', margin: 0, overflow: 'hidden' }}
                    >
                        {clip(statusText(record), null, { textAlign: 'center' })}
                    </Tag>
                )
            },
            {
                title: 'סכום',
                key: 'amount',
                width: 100,
                align: 'right',
                render: (_, record) => <span>{formatAmount(record.amount) || '—'}</span>
            },
            {
                title: 'מסמך',
                key: 'pdf',
                width: 80,
                align: 'center',
                render: (_, record) => {
                    if (record.txnfdid == null) return null
                    const key = this.pdfKey(record)
                    const downloading = !!pdfLoading[key]
                    return (
                        <Tooltip title="הורדת PDF">
                            <Button
                                shape="circle"
                                size="small"
                                icon="download"
                                loading={downloading}
                                disabled={downloading}
                                onClick={() => this.downloadPdf(record)}
                            />
                        </Tooltip>
                    )
                }
            }
        ]
        // sum of explicit column widths; without scroll.x the table doesn't scroll, it's just clipped by the screen edge
        const totalWidth = columns.reduce((sum, c) => sum + c.width, 0)
        return (
            <Row>
                {/* min-width: 0 — otherwise the flex/grid parent won't let the column shrink and the whole page scrolls */}
                <Col style={{ minWidth: 0 }}>
                    <div className="box">
                        <Table
                            className="center-table tranzila-table"
                            style={{ width: '100%' }}
                            scroll={{ x: totalWidth }}
                            rowKey="index"
                            size="middle"
                            bordered
                            pagination={{ current: page, pageSize: 20, onChange: setPage, hideOnSinglePage: true }}
                            rowClassName={(_, i) => (i % 2 ? 'row-odd' : 'row-even')}
                            columns={columns}
                            loading={loading}
                            dataSource={list}
                        />
                    </div>
                    <TransactionDetailsModal
                        visible={!!detailsTxn}
                        txn={detailsTxn}
                        items={items}
                        itemsLoading={itemsLoading}
                        itemsError={itemsError}
                        onClose={this.closeDetails}
                    />
                </Col>
            </Row>
        )
    }
}

export default withRouter(List)
